import React, { useState, useEffect } from 'react';
import { Lock, Mail, Eye, EyeOff, AlertCircle, Loader2, TrendingUp, Users, Shield, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Logo oficial de Capabilia
import capabiliaLogo from '../assets/capabilia-logo.png';

// Colores oficiales de Capabilia
const CAPABILIA = {
  green: '#00A878',
  greenDark: '#007A55',
  white: '#FFFFFF',
  grayLight: '#F5F5F5',
  textDark: '#1A1A1A'
};

// Estilos del logo para fondo verde (convierte a blanco puro)
const logoStyleWhite = {
  filter: 'brightness(0) invert(1)'
};

// Estilos del logo para fondo blanco (mantiene negro)
const logoStyleGreen = {
  filter: 'invert(1) sepia(1) saturate(5) hue-rotate(120deg) brightness(0.8)'
};

const LoginPage = () => {
  const { login, loginWithGoogle, ssoError, clearSsoError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Mostrar error de SSO si existe
  useEffect(() => {
    if (ssoError) {
      setError(ssoError);
      clearSsoError();
    }
  }, [ssoError, clearSsoError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      setError(err.message || 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setError('');
    setGoogleLoading(true);
    loginWithGoogle();
    // No necesitamos setGoogleLoading(false) porque la página redirige
  };

  const features = [
    { icon: TrendingUp, title: 'Evolución del Riesgo', desc: 'Seguimiento en tiempo real' },
    { icon: Users, title: 'Vista 360°', desc: 'Perfil completo del alumno' },
    { icon: Shield, title: 'Multi-Partner', desc: 'Gestión centralizada' },
    { icon: BarChart3, title: 'Analytics', desc: 'Métricas y KPIs' }
  ];

  return (
    <div className="min-h-screen flex">
      {/* Panel Izquierdo - Imagen y Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden"
        style={{ backgroundColor: CAPABILIA.green }}
      >
        {/* Imagen de fondo con overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1758270705317-3ef6142d306f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHx1bml2ZXJzaXR5JTIwc3R1ZGVudHMlMjBsYXB0b3AlMjBsZWFybmluZyUyMGVkdWNhdGlvbiUyMG1vZGVybnxlbnwwfHx8fDE3NzQxMTk0OTl8MA&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        {/* Overlay verde con gradiente */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: `linear-gradient(135deg, ${CAPABILIA.green}ee 0%, ${CAPABILIA.greenDark}dd 100%)`
          }}
        />
        
        {/* Contenido sobre la imagen */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo y Branding Superior */}
          <div>
            <div className="flex items-center gap-4 mb-2">
              {/* Logo oficial PNG con filtros CSS */}
              <img 
                src={capabiliaLogo} 
                alt="Capabilia" 
                style={{ 
                  width: '56px', 
                  height: '56px',
                  ...logoStyleWhite
                }}
              />
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Persistencia 360</h1>
                <p className="text-white/80 text-sm">by Capabilia</p>
              </div>
            </div>
          </div>
          
          {/* Mensaje Central */}
          <div className="max-w-lg">
            <h2 className="text-4xl xl:text-5xl font-bold leading-tight mb-6">
              Transforma datos en decisiones
            </h2>
            <p className="text-xl text-white/90 leading-relaxed mb-10">
              Monitorea el riesgo de abandono estudiantil y actúa a tiempo con insights accionables.
            </p>
            
            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, idx) => (
                <div 
                  key={idx} 
                  className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/15 transition-colors"
                >
                  <div className="p-2 bg-white/20 rounded-lg">
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{feature.title}</p>
                    <p className="text-white/70 text-xs">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between text-white/60 text-sm">
            <p>&copy; 2026 Capabilia. Todos los derechos reservados.</p>
            <div className="flex gap-4">
              <a href="#" className="hover:text-white transition-colors">Soporte</a>
              <a href="#" className="hover:text-white transition-colors">Privacidad</a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Panel Derecho - Formulario de Login */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo móvil (solo visible en pantallas pequeñas) */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
            <img 
              src={capabiliaLogo} 
              alt="Capabilia" 
              style={{ 
                width: '48px', 
                height: '48px',
                ...logoStyleGreen
              }}
            />
            <div>
              <h1 className="text-2xl font-bold" style={{ color: CAPABILIA.green }}>Persistencia 360</h1>
              <p className="text-gray-500 text-sm">by Capabilia</p>
            </div>
          </div>
          
          {/* Header del formulario */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Bienvenido de vuelta
            </h2>
            <p className="text-gray-500">
              Ingresa tus credenciales para acceder al portal
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-gray-50 hover:bg-gray-100 focus:bg-white"
                  style={{ '--tw-ring-color': CAPABILIA.green }}
                  placeholder="tu@email.com"
                  required
                  disabled={loading}
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-gray-50 hover:bg-gray-100 focus:bg-white"
                  style={{ '--tw-ring-color': CAPABILIA.green }}
                  placeholder="••••••••••"
                  required
                  disabled={loading}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  className="w-4 h-4 rounded border-gray-300 focus:ring-2"
                  style={{ accentColor: CAPABILIA.green }}
                />
                <span className="text-sm text-gray-600">Recordarme</span>
              </label>
              <a 
                href="#" 
                className="text-sm font-medium hover:underline"
                style={{ color: CAPABILIA.green }}
              >
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-4 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              style={{ 
                backgroundColor: CAPABILIA.green,
              }}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Ingresar'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">o continúa con</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Google SSO Button */}
          <button 
            onClick={handleGoogleLogin}
            disabled={loading || googleLoading}
            className="w-full py-3.5 px-4 border-2 border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="google-sso-btn"
          >
            {googleLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Conectando con Google...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Entrar con Google
              </>
            )}
          </button>

          {/* Help Text */}
          <p className="mt-8 text-center text-sm text-gray-500">
            ¿Necesitas ayuda? Contacta a{' '}
            <a 
              href="mailto:info@capabilia.org" 
              className="font-medium hover:underline"
              style={{ color: CAPABILIA.green }}
            >
              info@capabilia.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
